import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, Shield, CheckCircle, X, Plus, Trash2, Megaphone, ShieldOff, UserPlus, KeyRound, HardDrive, UserMinus, UserCog } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/formatBytes';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteOrganization,
  useOrganization,
  useUpdateOrganization,
  useOrgStorageUsage,
} from '@/api/organizations';
import {
  useChecklist,
  useAddChecklistItem,
  useUpdateChecklistItem,
  useDeleteChecklistItem,
} from '@/api/checklist';
import {
  useAnnouncements,
  useAnnouncementAcceptances,
  useCreateAnnouncement,
  useDeactivateAnnouncement,
} from '@/api/announcements';
import {
  useUsers,
  useInviteUser,
  useInvites,
  useResendInvite,
  useRevokeInvite,
  useDisableUser,
  useUpdateUser,
  useRemoveUserFromOrg,
  useResetUserMfa,
  useGenerateResetLink,
} from '@/api/users';
import { useWorkspaces } from '@/api/workspaces';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import { copyToClipboard } from '@/lib/clipboard';
import type { OrgStatus, Role } from '@/types/api';


export function OrganizationDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const canManage = hasRole(['sysadmin', 'manager']);
  const { data, isLoading, isError, error } = useOrganization(id);
  const workspaces = useWorkspaces(id);
  const update = useUpdateOrganization(id);
  const remove = useDeleteOrganization();
  const toast = useToast();
  usePageTitle(data?.name ?? 'Organization');

  type OrgTab = 'overview' | 'security' | 'checklist' | 'users' | 'announcements' | 'danger';
  const [tab, setTab] = useState<OrgTab>('overview');

  const [name, setName] = useState('');
  const [status, setStatus] = useState<OrgStatus>('active');
  const [email, setEmail] = useState('');
  const [quotaMb, setQuotaMb] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteUserModal, setDeleteUserModal] = useState<{ id: string; name: string } | null>(null);

  // Security & Compliance state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [ipCidrs, setIpCidrs] = useState('');
  const [baaSignedAt, setBaaSignedAt] = useState('');
  const [baaSignedBy, setBaaSignedBy] = useState('');
  const [retentionDays, setRetentionDays] = useState('');

  // Checklist state
  const [newChecklistLabel, setNewChecklistLabel] = useState('');
  const checklist = useChecklist(id);
  const addChecklistItem = useAddChecklistItem(id);
  const updateChecklistItem = useUpdateChecklistItem(id);
  const deleteChecklistItem = useDeleteChecklistItem(id);

  // Announcements state
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [expandedAnnId, setExpandedAnnId] = useState<string | null>(null);
  const announcements = useAnnouncements(id);
  const createAnnouncement = useCreateAnnouncement(id);
  const deactivateAnnouncement = useDeactivateAnnouncement(id);
  const acceptances = useAnnouncementAcceptances(id, expandedAnnId);

  // Users state
  const orgUsers = useUsers({ organization_id: id, limit: 100 });
  const orgInvites = useInvites({ organization_id: id });
  const resendInvite = useResendInvite();
  const revokeInvite = useRevokeInvite();
  const inviteUser = useInviteUser();
  const disableUser = useDisableUser();
  const updateUser = useUpdateUser();
  const removeFromOrg = useRemoveUserFromOrg();
  const resetMfa = useResetUserMfa();
  const generateResetLink = useGenerateResetLink();
  const [roleChangeModal, setRoleChangeModal] = useState<{ id: string; name: string; currentRole: Role } | null>(null);
  const [roleChangeValue, setRoleChangeValue] = useState<Role>('client_user');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('client_user');
  const [resetLink, setResetLink] = useState<{ url: string; name: string } | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  type OrgUserRow = NonNullable<typeof orgUsers.data>['items'][number];
  const [mobileActionTarget, setMobileActionTarget] = useState<OrgUserRow | null>(null);
  const pendingOrgInvites = (orgInvites.data ?? []).filter((inv) => !inv.accepted_at);

  const storage = useOrgStorageUsage(id);

  const isStaff = ['sysadmin', 'manager', 'staff'].includes(user?.role ?? '');
  const isSysadmin = user?.role === 'sysadmin';

  useEffect(() => {
    if (data) {
      setName(data.name);
      setStatus(data.status);
      setEmail(data.primary_contact_email ?? '');
      setQuotaMb(data.storage_quota_mb != null ? String(data.storage_quota_mb) : '');
      setMfaRequired(data.mfa_required ?? false);
      setIpCidrs((data.allowed_ip_cidrs ?? []).join('\n'));
      setBaaSignedAt(data.baa_signed_at ? data.baa_signed_at.slice(0, 10) : '');
      setBaaSignedBy(data.baa_signed_by ?? '');
      setRetentionDays(data.retention_days != null ? String(data.retention_days) : '');
    }
  }, [data]);

  // Reset the confirm field whenever the modal opens or org changes.
  useEffect(() => {
    if (deleteOpen) setDeleteConfirm('');
  }, [deleteOpen, data?.name]);

  const save = async () => {
    try {
      await update.mutateAsync({
        name,
        status,
        primary_contact_email: email || null,
        storage_quota_mb: quotaMb.trim() === '' ? null : parseInt(quotaMb, 10),
      });
      toast.success('Organization updated');
    } catch (e) {
      toast.error('Save failed', apiError(e));
    }
  };

  const saveIpAllowlist = async () => {
    const cidrs = ipCidrs
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await update.mutateAsync({ allowed_ip_cidrs: cidrs });
      toast.success('IP allowlist saved');
    } catch (e) {
      toast.error('Save failed', apiError(e));
    }
  };

  const saveBaa = async () => {
    try {
      await update.mutateAsync({
        baa_signed_at: baaSignedAt || null,
        baa_signed_by: baaSignedBy || null,
      });
      toast.success('BAA record saved');
    } catch (e) {
      toast.error('Save failed', apiError(e));
    }
  };

  const saveRetention = async () => {
    const days = retentionDays.trim() === '' ? null : parseInt(retentionDays, 10);
    try {
      await update.mutateAsync({ retention_days: days });
      toast.success('Retention policy saved');
    } catch (e) {
      toast.error('Save failed', apiError(e));
    }
  };

  const toggleMfa = async (val: boolean) => {
    setMfaRequired(val);
    try {
      await update.mutateAsync({ mfa_required: val });
      toast.success(val ? 'MFA requirement enabled' : 'MFA requirement disabled');
    } catch (e) {
      toast.error('Save failed', apiError(e));
      setMfaRequired(!val);
    }
  };

  const handleAddChecklist = async () => {
    if (!newChecklistLabel.trim()) return;
    try {
      await addChecklistItem.mutateAsync({ label: newChecklistLabel.trim() });
      setNewChecklistLabel('');
    } catch (e) {
      toast.error('Failed to add item', apiError(e));
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await inviteUser.mutateAsync({
        email: inviteEmail.trim(),
        role: inviteRole,
        organization_id: id,
        full_name: inviteFullName.trim() || undefined,
      });
      toast.success('Invitation sent');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteRole('client_user');
    } catch (e) {
      toast.error('Invite failed', apiError(e));
    }
  };

  const handleResendOrgInvite = async (inv: import('@/types/api').Invite) => {
    setResendingInviteId(inv.id);
    try {
      await resendInvite.mutateAsync({
        email: inv.email,
        role: inv.role as Role,
        organization_id: inv.organization_id,
      });
      toast.success(`Invite resent to ${inv.email}`);
    } catch (e) {
      toast.error('Resend failed', apiError(e));
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserModal) return;
    try {
      await removeFromOrg.mutateAsync({ userId: deleteUserModal.id, orgId: id });
      toast.success('User removed from organization');
      setDeleteUserModal(null);
      orgUsers.refetch();
    } catch (e) {
      toast.error('Failed to remove user', apiError(e));
    }
  };

  const handleGenerateResetLink = async (userId: string, name: string) => {
    try {
      const resp = await generateResetLink.mutateAsync(userId);
      setResetLink({ url: resp.reset_url, name });
    } catch (e) {
      toast.error('Failed to generate link', apiError(e));
    }
  };

  const handlePostAnnouncement = async () => {
    if (!newAnnouncement.trim()) return;
    try {
      await createAnnouncement.mutateAsync({ message: newAnnouncement.trim() });
      setNewAnnouncement('');
    } catch (e) {
      toast.error('Failed to post announcement', apiError(e));
    }
  };

  const handleDelete = async () => {
    if (!data) return;
    try {
      await remove.mutateAsync({ id, confirm: deleteConfirm.trim() });
      toast.success(`Deleted "${data.name}"`);
      setDeleteOpen(false);
      navigate('/admin/organizations');
    } catch (e) {
      toast.error('Delete failed', apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <Card>
          <div className="text-sm text-red-600">{apiError(error)}</div>
        </Card>
      ) : (
        <>
          {/* Tab bar */}
          <div className="flex gap-1 border-b border-[#E3E3F1] dark:border-[#2A2A4A] mb-4 flex-wrap">
            {([
              { id: 'overview', label: 'Overview' },
              { id: 'security', label: 'Security' },
              { id: 'checklist', label: 'Checklist' },
              { id: 'users', label: 'Users' },
              { id: 'announcements', label: 'Announcements' },
              ...(isSysadmin ? [{ id: 'danger', label: 'Danger zone' }] : []),
            ] as { id: OrgTab; label: string }[]).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  tab === t.id
                    ? t.id === 'danger' ? 'border-red-500 text-red-600' : 'border-[#0410BD] text-[#0410BD]'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-white',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && <>
          <Card title={data.name} actions={<Badge tone="green">{data.status}</Badge>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="o-name">Name</Label>
                <Input id="o-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="o-status">Status</Label>
                <Select
                  id="o-status"
                  value={status}
                  onChange={(v) => setStatus(v as OrgStatus)}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'suspended', label: 'Suspended' },
                    { value: 'archived', label: 'Archived' },
                  ]}
                  placeholder=""
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="o-email">Primary contact email</Label>
                <Input
                  id="o-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="o-quota">Storage quota (MB)</Label>
                <Input
                  id="o-quota"
                  type="number"
                  min={0}
                  value={quotaMb}
                  onChange={(e) => setQuotaMb(e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-4">
              {/* Storage usage */}
              <div className="flex items-center gap-2 text-sm text-[#676687]">
                <HardDrive size={14} className="shrink-0" />
                {storage.isLoading ? (
                  <span>Loading storage…</span>
                ) : storage.data ? (
                  <span>
                    <span className="font-semibold text-[#12122C]">{formatBytes(storage.data.used_bytes)}</span>
                    {storage.data.quota_bytes
                      ? ` of ${formatBytes(storage.data.quota_bytes)} used`
                      : ' used'}
                    {storage.data.quota_bytes && (
                      <span className="ml-2 inline-block h-1.5 w-24 bg-slate-200 rounded-full overflow-hidden align-middle">
                        <span
                          className="block h-full bg-[#0410BD] rounded-full"
                          style={{ width: `${Math.min(100, (storage.data.used_bytes / storage.data.quota_bytes) * 100)}%` }}
                        />
                      </span>
                    )}
                  </span>
                ) : null}
              </div>
              <Button variant="primary" onClick={save} loading={update.isPending} className="w-auto">
                Save changes
              </Button>
            </div>
          </Card>

          <Card title="Workspace">
            <p className="text-xs text-slate-500 mb-3">
              Each organization has one workspace — that's where its files and
              folders live. Click below to open it.
            </p>
            {workspaces.isLoading ? (
              <Spinner />
            ) : (workspaces.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-500">
                No workspace exists for this organization yet.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {(workspaces.data ?? []).map((w) => (
                  <li key={w.id} className="py-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge tone={w.status === 'active' ? 'green' : 'yellow'}>
                        {w.status}
                      </Badge>
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        Created {format(new Date(w.created_at), 'PP')}
                      </span>
                    </div>
                    <Link
                      to={`/workspace/${id}`}
                      className="text-sm text-bb-navy hover:underline whitespace-nowrap"
                    >
                      Open workspace →
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          </>}

          {tab === 'security' && <Card
            title={
              <span className="inline-flex items-center gap-2">
                <Shield size={15} /> Security &amp; Compliance
              </span>
            }
          >
            <div className="space-y-6">
              {/* Mandatory MFA */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-white">Require MFA for all users</div>
                    <div className="text-xs text-slate-500 dark:text-[#8888AA] mt-0.5">All users in this org must have MFA enabled to access the portal.</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={mfaRequired}
                    onClick={() => toggleMfa(!mfaRequired)}
                    disabled={update.isPending}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center border-2 border-transparent transition-colors focus:outline-none ${mfaRequired ? 'bg-bb-navy' : 'bg-slate-300 dark:bg-slate-600'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 bg-white shadow transform transition-transform ${mfaRequired ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>

              {/* IP Allowlist */}
              <div>
                <div className="text-sm font-medium text-slate-800 dark:text-white mb-1">IP Allowlist</div>
                <div className="text-xs text-slate-500 dark:text-[#8888AA] mb-2">One CIDR per line. Empty = no restriction.</div>
                <textarea
                  value={ipCidrs}
                  onChange={(e) => setIpCidrs(e.target.value)}
                  placeholder="e.g. 192.168.1.0/24"
                  rows={3}
                  className="w-full border border-bb-lilac dark:border-[#2A2A4A] bg-white dark:bg-[#1A1A2E] px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#8888AA] focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy font-mono"
                />
                <div className="mt-2 flex justify-end">
                  <Button variant="secondary" onClick={saveIpAllowlist} loading={update.isPending} className="text-xs w-auto">
                    Save IP allowlist
                  </Button>
                </div>
              </div>

              {/* BAA Tracking */}
              <div>
                <div className="text-sm font-medium text-slate-800 dark:text-white mb-1">
                  BAA Tracking
                  {data.baa_signed_at && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      <CheckCircle size={10} /> BAA on file
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 dark:text-[#8888AA] mb-1">Date signed</label>
                    <input
                      type="date"
                      value={baaSignedAt}
                      onChange={(e) => setBaaSignedAt(e.target.value)}
                      className="w-full border border-bb-lilac dark:border-[#2A2A4A] bg-white dark:bg-[#1A1A2E] px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 dark:text-[#8888AA] mb-1">Signed by</label>
                    <Input
                      value={baaSignedBy}
                      onChange={(e) => setBaaSignedBy(e.target.value)}
                      placeholder="Full name or entity"
                    />
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <Button variant="secondary" onClick={saveBaa} loading={update.isPending} className="text-xs w-auto">
                    Save BAA record
                  </Button>
                </div>
              </div>

              {/* Data Retention */}
              <div>
                <div className="text-sm font-medium text-slate-800 dark:text-white mb-1">Data Retention</div>
                <div className="text-xs text-slate-500 dark:text-[#8888AA] mb-2">Auto-purge deleted files after N days. Leave blank or set 0 to disable.</div>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={0}
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(e.target.value)}
                    placeholder="e.g. 90"
                    className="max-w-[120px]"
                  />
                  <span className="text-sm text-slate-500 dark:text-[#8888AA]">days</span>
                  <Button variant="secondary" onClick={saveRetention} loading={update.isPending} className="text-xs w-auto ml-auto">
                    Save retention
                  </Button>
                </div>
              </div>
            </div>
          </Card>}

          {tab === 'checklist' && <Card title="Document Checklist">
            {checklist.isLoading ? (
              <Spinner />
            ) : (checklist.data ?? []).length === 0 && !isStaff ? (
              <p className="text-sm text-slate-500 dark:text-[#8888AA]">No checklist items yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-[#2A2A4A]">
                {(checklist.data ?? []).map((item) => (
                  <li key={item.id} className="flex items-center gap-3 py-2">
                    <input
                      type="checkbox"
                      checked={item.is_checked}
                      onChange={(e) =>
                        updateChecklistItem.mutate({ itemId: item.id, is_checked: e.target.checked })
                      }
                      className="accent-bb-navy shrink-0"
                    />
                    <span
                      className={`flex-1 text-sm ${item.is_checked ? 'line-through text-slate-400 dark:text-[#8888AA]' : 'text-slate-800 dark:text-white'}`}
                    >
                      {item.label}
                    </span>
                    {isStaff && (
                      <button
                        type="button"
                        onClick={() => deleteChecklistItem.mutate({ itemId: item.id })}
                        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                        aria-label="Delete item"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {(checklist.data ?? []).length === 0 && isStaff && !checklist.isLoading && (
              <p className="text-sm text-slate-500 dark:text-[#8888AA] mb-3">No checklist items yet.</p>
            )}
            {isStaff && (
              <div className="mt-3 flex items-center gap-2">
                <Input
                  value={newChecklistLabel}
                  onChange={(e) => setNewChecklistLabel(e.target.value)}
                  placeholder="New item label"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleAddChecklist();
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={handleAddChecklist}
                  loading={addChecklistItem.isPending}
                  disabled={!newChecklistLabel.trim()}
                  className="text-xs w-auto shrink-0"
                >
                  <Plus size={14} />
                </Button>
              </div>
            )}
          </Card>}

          {tab === 'users' && <Card
            title="Users"
            actions={canManage ? (
              <Button variant="secondary" onClick={() => setInviteOpen(true)} className="text-xs">
                <UserPlus size={14} /> Invite user
              </Button>
            ) : undefined}
          >
            {orgUsers.isLoading ? (
              <Spinner />
            ) : (orgUsers.data?.items ?? []).length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-[#8888AA]">No users in this organization yet.</p>
            ) : (
              <>
                {/* Mobile card list */}
                <div className="sm:hidden divide-y divide-slate-100 dark:divide-[#2A2A4A] -mx-5">
                  {(orgUsers.data?.items ?? []).map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className="w-full text-left px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 dark:hover:bg-[#2A2A4A]/30 active:bg-slate-100"
                      onClick={() => setMobileActionTarget(u)}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 dark:text-[#C4C4DC] truncate">{u.full_name || u.email}</div>
                        {u.full_name && <div className="text-xs text-slate-500 dark:text-[#8888AA] truncate">{u.email}</div>}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-500 dark:text-[#8888AA] capitalize">{u.role.replace('_', ' ')}</span>
                          {u.is_active ? <Badge tone="green">Active</Badge> : <Badge tone="gray-strong">Disabled</Badge>}
                        </div>
                      </div>
                      <span className="text-slate-400 text-lg shrink-0">›</span>
                    </button>
                  ))}
                </div>
                {/* Desktop table */}
                <table className="hidden sm:table w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-[#2A2A4A]">
                      <th className="py-2 px-3 font-semibold">Name</th>
                      <th className="py-2 px-3 font-semibold">Email</th>
                      <th className="py-2 px-3 font-semibold">Role</th>
                      <th className="py-2 px-3 font-semibold">Status</th>
                      <th className="py-2 px-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orgUsers.data?.items ?? []).map((u) => (
                      <tr key={u.id} className="border-b border-slate-100 dark:border-[#2A2A4A] hover:bg-slate-50 dark:hover:bg-[#2A2A4A]/30">
                        <td className="py-2 px-3 text-slate-800 dark:text-[#C4C4DC]">{u.full_name || '—'}</td>
                        <td className="py-2 px-3 text-slate-600 dark:text-[#8888AA]">{u.email}</td>
                        <td className="py-2 px-3 text-slate-600 dark:text-[#8888AA] capitalize">{u.role.replace('_', ' ')}</td>
                        <td className="py-2 px-3">
                          {u.is_active ? <Badge tone="green">Active</Badge> : <Badge tone="gray-strong">Disabled</Badge>}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              title="Generate password reset link"
                              onClick={() => handleGenerateResetLink(u.id, u.full_name || u.email)}
                              className="p-1.5 text-slate-400 hover:text-[#0410BD] hover:bg-[#EFF0FF] dark:hover:bg-[#1E2040] rounded-[4px] transition-colors"
                            >
                              <KeyRound size={14} />
                            </button>
                            {u.mfa_enabled && (
                              <button
                                type="button"
                                title="Reset MFA"
                                onClick={() => resetMfa.mutate(u.id, {
                                  onSuccess: () => toast.success('MFA reset — user must re-enroll on next login'),
                                  onError: (e) => toast.error('Reset failed', apiError(e)),
                                })}
                                className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-[4px] transition-colors"
                              >
                                <ShieldOff size={14} />
                              </button>
                            )}
                            {isSysadmin && (
                              <button
                                type="button"
                                title="Change role"
                                onClick={() => {
                                  setRoleChangeValue(u.role);
                                  setRoleChangeModal({ id: u.id, name: u.full_name || u.email, currentRole: u.role });
                                }}
                                className="p-1.5 text-slate-400 hover:text-[#0410BD] hover:bg-[#EFF0FF] dark:hover:bg-[#1E2040] rounded-[4px] transition-colors"
                              >
                                <UserCog size={14} />
                              </button>
                            )}
                            <Button
                              variant="ghost"
                              onClick={() => disableUser.mutate(u.id, {
                                onSuccess: () => toast.success('User disabled'),
                                onError: (e) => toast.error('Failed', apiError(e)),
                              })}
                              disabled={!u.is_active || disableUser.isPending}
                              className="text-xs"
                            >
                              Disable
                            </Button>
                            <button
                              type="button"
                              title="Remove user from organization"
                              onClick={() => setDeleteUserModal({ id: u.id, name: u.full_name || u.email })}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-[4px] transition-colors"
                            >
                              <UserMinus size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {/* Pending invites — all roles, including client_admin and client_user */}
            {pendingOrgInvites.length > 0 && (
              <div className="mt-6 border-t border-slate-100 dark:border-[#2A2A4A] pt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-[#8888AA] mb-3">
                  Pending invites
                </div>
                <div className="space-y-2">
                  {pendingOrgInvites.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-50 dark:border-[#2A2A4A] last:border-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-slate-800 dark:text-[#C4C4DC] truncate">{inv.email}</span>
                          <Badge tone="yellow">Pending</Badge>
                        </div>
                        <div className="text-xs text-slate-500 dark:text-[#8888AA] mt-0.5 capitalize">
                          {inv.role.replace('_', ' ')} · expires {format(new Date(inv.expires_at), 'PP')}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          variant="secondary"
                          className="text-xs"
                          loading={resendingInviteId === inv.id}
                          disabled={resendingInviteId !== null || revokeInvite.isPending}
                          onClick={() => handleResendOrgInvite(inv)}
                        >
                          Resend
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={revokeInvite.isPending || resendingInviteId !== null}
                          onClick={() => {
                            if (confirm(`Revoke invite for ${inv.email}?`)) {
                              revokeInvite.mutate(inv.id, {
                                onSuccess: () => toast.success(`Invite for ${inv.email} revoked`),
                                onError: (e) => toast.error('Revoke failed', apiError(e)),
                              });
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>}

          {tab === 'announcements' && <Card
            title={
              <span className="inline-flex items-center gap-2">
                <Megaphone size={15} /> Announcements
              </span>
            }
          >
            {announcements.isLoading ? (
              <Spinner />
            ) : (
              <>
                {(announcements.data ?? []).filter((a) => a.is_active).length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-[#8888AA] mb-3">No active announcements.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-[#2A2A4A] mb-4">
                    {(announcements.data ?? [])
                      .filter((a) => a.is_active)
                      .map((ann) => {
                        const isExpanded = expandedAnnId === ann.id;
                        return (
                          <li key={ann.id} className="py-2">
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-800 dark:text-white">{ann.message}</p>
                                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                  <p className="text-xs text-slate-400 dark:text-[#8888AA]">
                                    {format(new Date(ann.created_at), 'PP')}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedAnnId(isExpanded ? null : ann.id)}
                                    className="text-xs text-[#0410BD] dark:text-[#818CF8] hover:underline"
                                  >
                                    {ann.accepted_count ?? 0} accepted
                                  </button>
                                </div>
                              </div>
                              {isStaff && (
                                <button
                                  type="button"
                                  onClick={() => deactivateAnnouncement.mutate({ id: ann.id })}
                                  className="p-1 text-slate-400 hover:text-red-500 transition-colors shrink-0"
                                  aria-label="Deactivate announcement"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                            {isExpanded && (
                              <div className="mt-2 ml-0 border border-slate-100 dark:border-[#2A2A4A] bg-slate-50 dark:bg-[#13132A] rounded p-3">
                                {acceptances.isLoading ? (
                                  <Spinner />
                                ) : (acceptances.data ?? []).length === 0 ? (
                                  <p className="text-xs text-slate-400 dark:text-[#8888AA]">No one has accepted this announcement yet.</p>
                                ) : (
                                  <ul className="space-y-1">
                                    {(acceptances.data ?? []).map((a) => (
                                      <li key={a.user_id} className="flex items-center justify-between gap-2 text-xs">
                                        <span className="text-slate-700 dark:text-[#C4C4DC] font-medium">
                                          {a.user_name ?? a.user_email ?? a.user_id}
                                        </span>
                                        <span className="text-slate-400 dark:text-[#8888AA] shrink-0">
                                          {format(new Date(a.accepted_at), 'PP p')}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                )}
                {isStaff && (
                  <div className="space-y-2">
                    <textarea
                      value={newAnnouncement}
                      onChange={(e) => setNewAnnouncement(e.target.value)}
                      placeholder="Write an announcement for this org's clients..."
                      rows={3}
                      className="w-full border border-bb-lilac dark:border-[#2A2A4A] bg-white dark:bg-[#1A1A2E] px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#8888AA] focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy"
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="primary"
                        onClick={handlePostAnnouncement}
                        loading={createAnnouncement.isPending}
                        disabled={!newAnnouncement.trim()}
                        className="text-xs w-auto"
                      >
                        Post announcement
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>}

          {tab === 'danger' && isSysadmin && (
            <Card
              title={
                <span className="inline-flex items-center gap-2 text-red-700">
                  <AlertTriangle size={16} /> Danger zone
                </span>
              }
            >
              <div className="flex items-start justify-between gap-4">
                <div className="text-sm text-slate-600 max-w-xl">
                  <p className="font-medium text-slate-800">Delete this organization</p>
                  <p className="mt-1">
                    Permanently removes the workspace, all folders, files, file
                    versions, requests, comments, notifications, invites, and
                    staff assignments for <span className="font-semibold">{data.name}</span>.
                    Audit log entries are preserved (with the org reference cleared).
                    This cannot be undone — archive instead if you might restore later.
                  </p>
                </div>
                <Button
                  variant="danger"
                  onClick={() => setDeleteOpen(true)}
                  className="text-xs whitespace-nowrap"
                >
                  Delete organization
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Mobile user action sheet */}
      <Modal
        open={!!mobileActionTarget}
        onClose={() => setMobileActionTarget(null)}
        title={mobileActionTarget?.full_name || mobileActionTarget?.email || 'User'}
      >
        {mobileActionTarget && (
          <div className="space-y-1 -mx-1">
            <p className="text-xs text-slate-500 dark:text-[#8888AA] mb-3 px-1">{mobileActionTarget.email}</p>
            <button
              type="button"
              className="w-full flex items-center gap-3 px-3 py-3 text-sm text-slate-700 dark:text-[#C4C4DC] hover:bg-slate-50 dark:hover:bg-[#2A2A4A]/30 rounded-lg"
              onClick={() => {
                handleGenerateResetLink(mobileActionTarget.id, mobileActionTarget.full_name || mobileActionTarget.email);
                setMobileActionTarget(null);
              }}
            >
              <KeyRound size={16} className="text-slate-400" /> Reset password
            </button>
            {mobileActionTarget.mfa_enabled && (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-3 py-3 text-sm text-slate-700 dark:text-[#C4C4DC] hover:bg-slate-50 dark:hover:bg-[#2A2A4A]/30 rounded-lg"
                onClick={() => {
                  resetMfa.mutate(mobileActionTarget.id, {
                    onSuccess: () => toast.success('MFA reset — user must re-enroll on next login'),
                    onError: (e) => toast.error('Reset failed', apiError(e)),
                  });
                  setMobileActionTarget(null);
                }}
              >
                <ShieldOff size={16} className="text-slate-400" /> Reset 2FA
              </button>
            )}
            {mobileActionTarget.is_active && (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-3 py-3 text-sm text-slate-700 dark:text-[#C4C4DC] hover:bg-slate-50 dark:hover:bg-[#2A2A4A]/30 rounded-lg"
                onClick={() => {
                  disableUser.mutate(mobileActionTarget.id, {
                    onSuccess: () => toast.success('User disabled'),
                    onError: (e) => toast.error('Failed', apiError(e)),
                  });
                  setMobileActionTarget(null);
                }}
              >
                <UserMinus size={16} className="text-slate-400" /> Disable user
              </button>
            )}
            <button
              type="button"
              className="w-full flex items-center gap-3 px-3 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              onClick={() => {
                setDeleteUserModal({ id: mobileActionTarget.id, name: mobileActionTarget.full_name || mobileActionTarget.email });
                setMobileActionTarget(null);
              }}
            >
              <Trash2 size={16} /> Remove from organization
            </button>
          </div>
        )}
      </Modal>

      {/* Change role modal */}
      <Modal
        open={!!roleChangeModal}
        onClose={() => setRoleChangeModal(null)}
        title="Change role"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRoleChangeModal(null)}>Cancel</Button>
            <Button
              variant="primary"
              loading={updateUser.isPending}
              disabled={roleChangeValue === roleChangeModal?.currentRole}
              onClick={() => {
                if (!roleChangeModal) return;
                updateUser.mutate(
                  { id: roleChangeModal.id, role: roleChangeValue },
                  {
                    onSuccess: () => {
                      toast.success(`Role updated to ${roleChangeValue.replace('_', ' ')}`);
                      setRoleChangeModal(null);
                    },
                    onError: (e) => toast.error('Role change failed', apiError(e)),
                  },
                );
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700 mb-4">
          Change role for <strong>{roleChangeModal?.name}</strong>.
        </p>
        <Select
          value={roleChangeValue}
          onChange={(v) => setRoleChangeValue(v as Role)}
          options={[
            { value: 'client_user', label: 'Client User' },
            { value: 'client_admin', label: 'Client Admin' },
          ]}
        />
      </Modal>

      {/* Delete user modal */}
      <Modal
        open={!!deleteUserModal}
        onClose={() => setDeleteUserModal(null)}
        title="Remove user"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteUserModal(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteUser} loading={removeFromOrg.isPending} className="w-auto">
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Remove <strong>{deleteUserModal?.name}</strong> from this organization? They will lose access immediately.
        </p>
      </Modal>

      {/* Invite user modal */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite user to organization"
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleInvite} loading={inviteUser.isPending} disabled={!inviteEmail.trim()} className="w-auto">
              Send invite
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="inv-email">Email</Label>
            <Input id="inv-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inv-name">Full name (optional)</Label>
            <Input id="inv-name" value={inviteFullName} onChange={(e) => setInviteFullName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inv-role">Role</Label>
            <Select
              id="inv-role"
              value={inviteRole}
              onChange={(v) => setInviteRole(v as Role)}
              options={[
                { value: 'client_admin', label: 'Client admin' },
                { value: 'client_user', label: 'Client user' },
              ]}
              placeholder=""
            />
          </div>
        </div>
      </Modal>

      {/* Password reset link modal */}
      <Modal
        open={!!resetLink}
        onClose={() => setResetLink(null)}
        title="Password reset link"
        footer={
          <>
            <Button variant="secondary" onClick={() => setResetLink(null)}>Close</Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (resetLink) {
                  await copyToClipboard(resetLink.url);
                  toast.success('Link copied to clipboard');
                }
              }}
              className="w-auto"
            >
              Copy link
            </Button>
          </>
        }
      >
        {resetLink && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-[#8888AA]">
              Share this link with <strong>{resetLink.name}</strong>. It expires in 24 hours and can only be used once.
            </p>
            <div className="bg-slate-50 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#2A2A4A] rounded px-3 py-2">
              <p className="text-xs font-mono break-all text-slate-700 dark:text-[#C4C4DC]">{resetLink.url}</p>
            </div>
          </div>
        )}
      </Modal>

      {data && (
        <Modal
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          title="Delete organization"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleteConfirm.trim().toLowerCase() !== data.name.trim().toLowerCase() || remove.isPending}
                loading={remove.isPending}
              >
                I understand — delete it
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              This will permanently delete <span className="font-semibold">{data.name}</span>{' '}
              and everything in its workspace. This action cannot be undone.
            </p>
            <div>
              <Label htmlFor="del-confirm">
                Type <span className="font-mono font-semibold">{data.name}</span> to confirm
              </Label>
              <Input
                id="del-confirm"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
